import React from 'react';
import PropTypes from 'prop-types';
import { t } from 'ttag';
import { RiLock2Fill } from 'react-icons/ri';

import { Button, Text } from '@components';
import { mergeClasses } from '@utils';

import {
  FRAME_GRADIENT,
  GLOW,
  MUTED_FRAME_GRADIENT,
  MUTED_GLOW,
  getFrameGlow
} from '../theme';
import { formatPrizeDay, formatUpcomingDay, getPrizeState } from '../utils';
import PrizeStatus, { chipClassName } from './PrizeStatus';
import prizePropType from './prizePropType';

// Door content fades in one piece after another once the door (300ms) has opened.
const REVEAL_DELAYS = [
  'delay-[300ms]',
  'delay-[380ms]',
  'delay-[460ms]',
  'delay-[540ms]'
];

const getRevealClassName = (isOpen, step) =>
  mergeClasses(
    'translate-y-3 opacity-0 transition-[opacity,transform] duration-150 motion-reduce:translate-y-0 motion-reduce:transition-none',
    isOpen && ['translate-y-0 opacity-100 duration-300', REVEAL_DELAYS[step]]
  );

/**
 * A single prize day, in a gradient frame that glows while the door is open. Both are
 * purple while the prize is active, otherwise grey. The API serves the image for the prize's
 * status: in colour when active, greyscale when expired and blurred when upcoming.
 */
export default function PrizeDoor({
  prize,
  isOpen,
  getButtonText,
  onOpen,
  onParticipate,
  onDetails
}) {
  const { id, attributes } = prize;
  const state = getPrizeState(attributes);
  const bodyId = `prize-door-${id}`;
  const revealDay = formatUpcomingDay(attributes.valid_from);
  const isActive = attributes.status === 'Active';
  const glow = isActive ? GLOW : MUTED_GLOW;

  return (
    <div
      className={mergeClasses(
        'relative shrink-0 basis-[60px] rounded-xl transition-[flex,min-width,box-shadow] duration-300 ease-out motion-reduce:transition-none lg:min-w-[56px] lg:shrink lg:grow lg:basis-0 lg:snap-start',
        { 'basis-[470px] lg:min-w-[420px] lg:grow-[6.5]': isOpen }
      )}
      style={{
        backgroundImage: isActive ? FRAME_GRADIENT : MUTED_FRAME_GRADIENT,
        boxShadow: isOpen ? getFrameGlow(glow) : undefined
      }}
    >
      <div className="absolute inset-[3px] overflow-hidden rounded-[9px] bg-night-rider">
        {attributes.image && (
          <img
            src={attributes.image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-[50%_25%] lg:object-top"
          />
        )}
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={bodyId}
          tabIndex={isOpen ? -1 : 0}
          onClick={onOpen}
          className={mergeClasses(
            'absolute inset-0 m-0 flex w-full cursor-pointer items-center justify-between gap-2 border-0 bg-black/60 px-4 text-white transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white lg:flex-col lg:px-0 lg:py-5',
            { 'pointer-events-none opacity-0': isOpen }
          )}
        >
          <span className="text-xs font-semibold uppercase tracking-wider lg:rotate-180 lg:text-sm lg:[writing-mode:vertical-rl]">
            {formatPrizeDay(attributes.valid_from)}
          </span>
          <PrizeStatus attributes={attributes} compact />
        </button>
        <div
          id={bodyId}
          className={mergeClasses(
            // A size container, so the text scales with the door's width rather than the screen's.
            'invisible absolute inset-0 flex flex-col items-start justify-end gap-2.5 bg-gradient-to-t from-black/95 via-black/70 via-40% to-transparent to-70% p-5 text-white opacity-0 transition-[opacity,visibility] duration-150 @container @xl:gap-3.5 @xl:p-8 motion-reduce:transition-none',
            { 'visible opacity-100 duration-300': isOpen }
          )}
        >
          {state === 'upcoming' ? (
            <Text
              className={mergeClasses(
                'flex items-center gap-2 font-semibold text-white',
                getRevealClassName(isOpen, 0)
              )}
            >
              <RiLock2Fill className="h-4 w-4 shrink-0" />
              {t`Prize revealed ${revealDay}`}
            </Text>
          ) : (
            <>
              <div
                className={mergeClasses(
                  'flex flex-wrap gap-1.5',
                  getRevealClassName(isOpen, 0)
                )}
              >
                <PrizeStatus attributes={attributes} />
                <span className={chipClassName}>
                  {formatPrizeDay(attributes.valid_from)}
                </span>
              </div>
              {/* A plain h3: Heading's breakpoint font sizes would override the container sizes. */}
              <h3
                className={mergeClasses(
                  'm-0 max-w-full hyphens-auto break-words text-2xl font-extrabold uppercase leading-none text-white @xl:text-[28px] @3xl:text-[34px]',
                  getRevealClassName(isOpen, 1)
                )}
              >
                {attributes.title}
              </h3>
              <Text
                className={mergeClasses(
                  'line-clamp-3 max-w-[46ch] text-sm text-white/80 @xl:line-clamp-4 @xl:text-base',
                  getRevealClassName(isOpen, 2)
                )}
              >
                {attributes.description}
              </Text>
              <div
                className={mergeClasses(
                  'mt-1 flex items-center gap-4',
                  getRevealClassName(isOpen, 3)
                )}
              >
                <Button
                  size="md"
                  onClick={() => onParticipate(id)}
                  disabled={state !== 'today'}
                >
                  <Button.Text>{getButtonText(attributes)}</Button.Text>
                </Button>
                <button
                  type="button"
                  onClick={() => onDetails(prize)}
                  className="m-0 cursor-pointer border-0 bg-transparent p-0 font-semibold text-white underline underline-offset-4"
                >
                  {t`Details`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

PrizeDoor.propTypes = {
  prize: prizePropType.isRequired,
  isOpen: PropTypes.bool.isRequired,
  getButtonText: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onParticipate: PropTypes.func.isRequired,
  onDetails: PropTypes.func.isRequired
};
